<script lang="ts">
    import { computed, reactive, ref } from 'vue';
    import vm, { JourneyTextNode } from '@journeys/Journeys.viewmodel';
    import { TextNodeVariation, TextNodeAlign } from '@journeys/Enums';

    // components
    import FluidButton from '@cwd/shared/components/FluidButton.vue';
    import ContainerFloating from '@shared/components/ContainerFloating.vue';

    // assets
    import { FontStyleBoldSVG, FontStyleItalicSVG, ArrowLeftSVG, ArrowRightSVG } from '@shared/assets/vector/ui/IconsUI';
    import { usePointer } from '@shared/utils/UseEvent';
    import { requestFrame } from '@shared/utils';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'TextNodeContextMenu',
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const props = defineProps<{
            node: JourneyTextNode;
        }>(),
        { editor, nav, settings } = vm,
        state = reactive({
            fontColorPicker: false,
        });

    let cachedStyling = '';
    const computedStyles = computed(() => {
        if (editor.state.busy) return cachedStyling;

        let node = props.node;
        return (cachedStyling = `--pos-y: ${node.y - editor.tools.sel_areaPadding - 60}px; --pos-x: ${node.x + (node.w || node.runtime?.w || 0) / 2 - editor.tools.sel_areaPadding}px; --zscale: ${1 * ((nav.p - nav.z) / nav.p)};`);
    });

    const handleToggleFontStyleBold = () => {
        props.node.fw = props.node.fw === 600 ? 400 : 600;
    };

    const handleToggleFontStyleItalic = () => {
        props.node.fv = props.node.fv === 1 ? 0 : 1;
    };

    const getRuntimeDims = () => {
        props.node.runtime!.h = props.node.runtime!.el.offsetHeight;
        props.node.runtime!.w = props.node.runtime!.el.offsetWidth;
    };

    const minFontSize = computed(() => settings.editor.textNode.minFontSize),
        maxFontSize = computed(() => settings.editor.textNode.maxFontSize);
    let lastMod = 0;
    const adjustFontSize = (mod: number) => {
        if ((mod > 0 && props.node.fs < maxFontSize.value) || (mod < 0 && props.node.fs > minFontSize.value)) {
            props.node.fs += mod;
            props.node.x -= lastMod === mod ? mod : 0;
        }

        getRuntimeDims();
        lastMod = mod;
    };

    const dragAdjustFontSize = (ev: PointerEvent) => {
        let fs = props.node.fs,
            x = props.node.x;

        usePointer.move(({ offset }) => {
            let val = (offset.x - (offset.x % settings.editor.incrementDragSensitivity)) / settings.editor.incrementDragSensitivity;

            if (fs + val >= maxFontSize.value) props.node.fs = maxFontSize.value;
            else if (fs + val <= minFontSize.value) props.node.fs = minFontSize.value;
            else {
                props.node.fs = fs + val;

                if (!props.node.w) {
                    props.node.x = x - val;
                    getRuntimeDims();
                }
            }
        }, ev);
    };

    let heroId = ref(props.node.id);
    const heroSynced = computed(() => {
        if (heroId.value !== props.node.id)
            requestFrame(() => {
                state.fontColorPicker = false;
                heroId.value = props.node.id;
            });
        return heroId.value === props.node.id;
    });
</script>

<template>
    <ContainerFloating class="horizontal canvas-menu" v-if="heroSynced" :style="computedStyles">
        <FluidButton class="btn btn-default" :icon="FontStyleBoldSVG()" @click="handleToggleFontStyleBold" :class="node.fw === 600 && 'active'" />
        <FluidButton
            class="btn btn-default"
            :icon="FontStyleItalicSVG()"
            @click="handleToggleFontStyleItalic"
            :class="node.fv === TextNodeVariation.Italic && 'active'" />

        <span class="separator vertical" />

        <div class="settings-group horizontal fl" v-if="editor.tools.sel_keyObj">
            <ArrowLeftSVG class="control-arrow" @click="adjustFontSize(-1)" />
            <span class="fw-600" @pointerdown.left.stop="dragAdjustFontSize">{{ node.fs }}</span>
            <ArrowRightSVG class="control-arrow" @click="adjustFontSize(+1)" />
        </div>
    </ContainerFloating>
</template>

<style>
    .cp-swatch-container {
        position: relative;
        display: flex;
        height: 1.75rem;
        width: 1.75rem;
        padding: 6px;
    }

    .canvas-menu {
        --zscale: 1;
        --pos-x: 0px;
        --pos-y: 0px;
        top: 0;
        left: 0;
        transform: translate3d(calc(var(--pos-x) - 50%), var(--pos-y), 0) scale(var(--zscale));
        transform-origin: bottom;
        transition-property: transform;
        transition-duration: var(--td-fast);
        transition-timing-function: ease-out;

        will-change: transform;
    }
</style>
